import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Dropdown from '../../../../../components/Dropdown';
import { MuiTextField } from '../../../../../components/MuiComponents/FormInputs';
import ErrorBanner from '../../../../../components/MuiComponents/MuiTextFieldErrorBanner';
import Button from '../../../../../components/Button';
import MuiTooltip from '../../../../../components/MuiComponents/MuiTooltip';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { AlertContext } from '../../../../../components/SuccessOrFailedModal';
import { LayoutContext } from '../../../../../Providers/LayoutProvider';
import {
  useScenarios,
  useScenario,
  useDeleteScenario,
  useValidateScenario,
} from '../../../../../hooks/siembagpt';
import {
  SCENARIO_MIN_CHARS,
  SCENARIO_MAX_CHARS,
  SCENARIO_MAX_ATTEMPTS,
  SCENARIO_FEATURE_NAME_MAX_CHARS,
  SCENARIO_STEP_MAX_CHARS,
  sanitizeLlmText,
} from '../../../../../utils/constants/genpt';
import ScenarioStatusBadge from './ScenarioStatusBadge';
import {
  scenarioTrimmedLength,
  isScenarioDescriptionValid,
  scenarioValidatePayload,
  nextAttemptsRemaining,
  attemptsExhausted,
  scenarioAttemptText,
  confirmedScenarioFields,
  scenarioFieldsFromSavedRow,
  hasScenarioCandidates,
  selectedScenarioCandidate,
  renameScenarioCandidate,
  normalizeScenarioName,
  scenarioNameCollides,
  dedupeScenarioOptions,
} from '../utils/scenario';

const ADD_NEW_VALUE = 'add-new';

/**
 * Tooltip copy for the attempts pill's info icon (ROAR-4923 #4). Reworded for ROAR-4986: only a
 * scenario that passes validation spends an attempt — a blocked or unusable one is free.
 */
const ATTEMPTS_TOOLTIP =
  'Only a scenario that passes validation uses an attempt — blocked or invalid tries are free. You have 5 attempts to finalize your scenario.';

/**
 * "N ATTEMPTS LEFT" pill, pinned top-right of the card header (Figma 05a/06/10). Amber normally;
 * turns red once no attempts remain (ROAR-4923 #2). The info icon is wrapped in a tooltip so it is
 * hoverable and shows the attempts-usage note (ROAR-4923 #4).
 */
function AttemptsChip({ count }) {
  const isExhausted = count === 0;
  const colorClass = isExhausted ? 'bg-[#F65C5C33] text-[#F65C5C]' : 'bg-[#FFDFB2] text-[#8A6D3B]';
  return (
    <span
      className={`ml-auto flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold ${colorClass}`}
    >
      {count} ATTEMPTS LEFT
      <MuiTooltip title={ATTEMPTS_TOOLTIP}>
        <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'pointer' }} />
      </MuiTooltip>
    </span>
  );
}

/**
 * The "How to write a good scenario" toggle (Figma frames 02/02b) — a small teal control (book icon +
 * label + chevron). Split from its tips panel (ROAR-4950) so the panel can render full-width below the
 * toggle/attempts-chip row instead of being trapped as a shrinking flex item narrower than the
 * textarea. `ScenarioTipsPanel` is the paired panel; both are driven by the parent `showTips` state.
 */
function ScenarioTipsToggle({ expanded, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="flex items-center gap-1 text-xs font-semibold text-primary-500"
    >
      <MenuBookOutlinedIcon sx={{ fontSize: 16 }} />
      <span>How to write a good scenario</span>
      {expanded ? (
        <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
      ) : (
        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
      )}
    </button>
  );
}

/**
 * The light-teal tips list shown when the toggle is open. Rendered as a full-width block so it matches
 * the textarea width (ROAR-4950). `list-outside` + `pl-6` give the bullets a clean hanging indent —
 * wrapped lines align under the text, not under the disc (which `list-inside` had caused).
 */
function ScenarioTipsPanel() {
  return (
    <ul className="mt-2 rounded-md bg-primary-50 py-3 pr-4 pl-6 text-xs opacity-80 space-y-2 list-disc list-outside">
      <li>
        Include the target URL (e.g: &#8220;Navigate to the login page that is:
        https://assetname.com/login)
      </li>
      <li>Describe the specific feature to test (e.g: &#8220;Test the login user flow&#8221; )</li>
      <li>
        Authentication: If this is an authenticated flow then the credentials above will be used
        automatically. You do not need to include the credentials in the input scenario. Instead,
        type use the given credentials to authenticate.
      </li>
      <li>
        Keep your scenario focused on one feature or flow. For multi-step flows, specify which one
        to test. (e.g. &#8216;Test the checkout flow&#8217; or &#8216;Test the login flow.)
      </li>
    </ul>
  );
}

/**
 * Previous Attempts collapsible (Figma frame 07). Compact custom collapsible (not the generic
 * MuiAccordion): a light header band with a clock icon, "PREVIOUS ATTEMPTS", a count badge and a
 * chevron; the body lists each attempt with an amber "ATTEMPT N:" label above its text.
 */
function PreviousAttempts({ attempts, expanded, onToggle }) {
  return (
    <div className="rounded-md border border-[#EFEFEF] overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 bg-[#F7F8FA] px-3 py-2"
      >
        <AccessTimeIcon sx={{ fontSize: 16 }} className="text-grey3" />
        <span className="text-xs font-semibold uppercase text-grey3">Previous Attempts</span>
        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#EFEFEF] px-1.5 text-[10px] font-semibold text-grey3">
          {attempts.length}
        </span>
        {expanded ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 16 }} className="ml-auto text-grey3" />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} className="ml-auto text-grey3" />
        )}
      </button>
      {expanded && (
        <div className="space-y-3 px-3 py-3">
          {attempts.map((attempt, idx) => (
            <div key={idx}>
              <p className="text-[10px] font-semibold uppercase text-[#8A6D3B]">
                Attempt {idx + 1}:
              </p>
              <p className="mt-0.5 text-xs text-grey3">{scenarioAttemptText(attempt)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * In-field validating loader (Figma frame 05) — replaces the textarea while a VALIDATE request is in
 * flight so the field never looks idle mid-call (the reported "crash" was the missing loading UI).
 */
function ScenarioValidatingLoader() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center justify-center text-center py-10"
    >
      <AutorenewIcon className="animate-spin" sx={{ color: 'var(--primary-500)', fontSize: 28 }} />
      <div className="mt-3 font-semibold text-primary-500">
        Validating &amp; Extracting Navigation Steps
      </div>
      <div className="mt-1 text-xs text-grey3">Checking executability of the scenario</div>
    </div>
  );
}

/**
 * Define Scenario section + guardrail loop (ROAR-4840, plan D3/D4/D6, AC1/AC3/AC4/AC5/AC7/AC11/AC12a).
 *
 * Composition only, per the plan's reuse-first mandate: `Dropdown` (saved scenarios, mirroring the
 * saved-credentials pattern), local `ScenarioTips` / `PreviousAttempts` collapsibles, `MuiTextField`
 * (multiline, with a live character counter), `MuiTextFieldErrorBanner` (guardrail-blocked message),
 * and `popup()` (delete confirmation). Rendered by `TestSetup` after Access Level, before Throttling,
 * only while `testType === 'scenario'` — mounting/unmounting this component IS the reset when the user
 * switches coverage away from and back to Business Scenario (plan §5 "Coverage switched away").
 *
 * `feature_name` and `steps[]` are untrusted LLM-authored text (plan D6) — rendered as plain text only,
 * never `dangerouslySetInnerHTML`.
 *
 * @param {string} assetId Asset the scenario is scoped to (saved-scenario list + create).
 * @param {string} chatId Chat id, part of every `/genpt/test/steps` guardrail call.
 * @param {string} nonce Current `START_TEST` / re-issued `SCENARIO_VALIDATE` nonce (plan D3). Read
 *   fresh from the caller's `msg.metadata.nonce` on every render — this component holds no nonce state
 *   of its own, matching the rest of the step-machine widgets (`RedirectionWidget`, `StatusCodeWidget`).
 * @param {number|null} [serverAttemptsRemaining] Authoritative "attempts left" from the server, read
 *   off the open message's metadata (`msg.metadata.attempts_remaining`) exactly like `nonce`. The
 *   ledger is per-CHAT and lives in Redis, so without this a remount (reload, Test Setup collapse)
 *   would re-seed the chip at the full budget over a partly spent ledger (ROAR-4986).
 * @param {boolean} [isDisabled] Disable all interaction (e.g. after declaration submitted).
 * @param {boolean} [isReadOnly] Render a static summary instead of the interactive flow.
 * @param {object|null} [initialScenario] Optional prefill `{ scenarioId, description, featureName, steps, confirmed }`.
 * @param {(state: object) => void} onChange Called with `{ confirmed, scenarioId, description, featureName, steps }`
 *   on every meaningful change so `TestSetup` can gate its Continue button and fold the fields into `configData`.
 */
export default function DefineScenarioSection({
  assetId = null,
  chatId = null,
  nonce = null,
  serverAttemptsRemaining = null,
  isDisabled = false,
  isReadOnly = false,
  initialScenario = null,
  onChange = () => {},
  // Credentials from the parent Test Setup form, forwarded on the validate call so api-backend can
  // resolve credential-presence booleans (keeps authenticated / MFA scenarios from false-blocking).
  // `savedCredential` is the id when a saved credential is selected instead of typed creds.
  username = '',
  password = '',
  mfaSecretKey = '',
  savedCredential = null,
}) {
  const [popup] = useContext(AlertContext);
  const navigate = useNavigate();
  const { mainUrl } = useContext(LayoutContext);

  const [selectedValue, setSelectedValue] = useState(initialScenario?.scenarioId || ADD_NEW_VALUE);
  const [description, setDescription] = useState(initialScenario?.description || '');
  // Shown "at all times" per AC7 — seeded from the server's count when the chat already carries one
  // (ROAR-4986), else the full budget, rather than hiding the chip until something is submitted.
  const [attemptsRemaining, setAttemptsRemaining] = useState(
    Number.isFinite(serverAttemptsRemaining) ? serverAttemptsRemaining : SCENARIO_MAX_ATTEMPTS
  );
  const [previousAttempts, setPreviousAttempts] = useState([]);
  const [guardrailMessage, setGuardrailMessage] = useState(null);
  // Sanitized defensively here too (not just at `scenarioFieldsFromSavedRow`, the mapper that
  // normally feeds `initialScenario`): this is the last hop before `steps`/`featureName` render as
  // plain text, and there is no error boundary anywhere to catch a non-string LLM-authored entry
  // slipping through as `<span>{step}</span>` (the "View Setup Details" crash this guards against).
  const [extractedSteps, setExtractedSteps] = useState(
    Array.isArray(initialScenario?.steps)
      ? initialScenario.steps
          .map((step) => sanitizeLlmText(step, SCENARIO_STEP_MAX_CHARS))
          .filter(Boolean)
      : []
  );
  const [featureName, setFeatureName] = useState(
    sanitizeLlmText(initialScenario?.featureName || '', SCENARIO_FEATURE_NAME_MAX_CHARS)
  );
  const [confirmed, setConfirmed] = useState(Boolean(initialScenario?.confirmed));
  const [editingName, setEditingName] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [showPreviousAttempts, setShowPreviousAttempts] = useState(false);
  // AC6 sub-scenario picker (ROAR-4842, Figma frame 06a) — a validation response can split into 2-4
  // candidates instead of a single feature_name/steps result. `candidates` is only ever non-empty
  // between a split VALIDATE response and the following Confirm/Edit.
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState(0);
  const [editingCandidateIndex, setEditingCandidateIndex] = useState(null);
  // AC11: when a SAVED scenario's description is edited and re-validated, Confirm asks whether to
  // update the existing record in place ('update') or save a fresh one ('new'). null for Add-New
  // and for a plain Run-Again where the description was not changed.
  const [saveMode, setSaveMode] = useState(null);

  // The read-only summary needs none of this — it only reads `initialScenario` — so skip the network
  // calls entirely there (isReadOnly renders appear e.g. once per row in the Activities "View Setup
  // Details" modal).
  const { data: scenariosData } = useScenarios(assetId, { enabled: !isReadOnly });
  const { data: scenarioDetail } = useScenario(
    selectedValue !== ADD_NEW_VALUE ? selectedValue : undefined,
    { enabled: !isReadOnly }
  );
  const deleteScenario = useDeleteScenario();
  const validateScenario = useValidateScenario();

  // ROAR-4986: follow the server's count whenever the open message's metadata carries a new one
  // (api-backend merges `attempts_remaining` into it on every verdict). This is what keeps the chip
  // honest across a remount/reload, and it never fights the in-flight update in `handleValidate` —
  // both end up on the same server number.
  useEffect(() => {
    if (Number.isFinite(serverAttemptsRemaining)) {
      setAttemptsRemaining(serverAttemptsRemaining);
    }
  }, [serverAttemptsRemaining]);

  // Selecting a saved scenario shows its saved description/name/steps immediately (mirrors
  // SavedScenarioPicker). The attempt ledger is per-CHAT and lives server-side, so it is NOT reset
  // here (ROAR-4986) — only the scenario fields are.
  useEffect(() => {
    if (selectedValue === ADD_NEW_VALUE) return;
    const scenario = scenarioDetail?.scenario;
    if (!scenario) return;
    const fields = scenarioFieldsFromSavedRow(scenario);
    setDescription(fields.scenarioDescription);
    setFeatureName(fields.featureName);
    setExtractedSteps(fields.scenarioSteps);
    setGuardrailMessage(null);
    setConfirmed(false);
    setCandidates([]);
    setSelectedCandidateIndex(0);
    setSaveMode(null);
  }, [selectedValue, scenarioDetail]);

  const handleSelectChange = (option) => {
    setSelectedValue(option?.value ?? ADD_NEW_VALUE);
    if ((option?.value ?? ADD_NEW_VALUE) === ADD_NEW_VALUE) {
      setDescription('');
      setFeatureName('');
      setExtractedSteps([]);
      setGuardrailMessage(null);
      setConfirmed(false);
      setCandidates([]);
      setSelectedCandidateIndex(0);
      setSaveMode(null);
    }
  };

  const handleDeleteClick = (scenario) => {
    // The shared confirm modal resolves its promise (undefined) only on "Delete"; Cancel/close never
    // resolves. So the delete must run unconditionally inside `.then()` — the previous
    // `if (!confirmedDelete) return;` guard read the `undefined` value as falsy and silently swallowed
    // every delete (ROAR-4923/ROAR-4511 bug). Matches the repo pattern in AddBlockerModal.
    popup({
      title: 'Delete saved scenario',
      variant: 'warning',
      description: `Delete "${scenario.name || scenario.feature_name}"? Reports already generated from it are retained.`,
      okayText: 'Delete',
      cancelText: 'Cancel',
    }).then(() => {
      deleteScenario.mutate(scenario.id, {
        onSuccess: () => {
          // The scenario just removed may be the one currently selected — reset to Add New so the
          // section never points at a deleted record (plan §5 "Delete the currently-selected scenario").
          if (String(selectedValue) === String(scenario.id)) {
            handleSelectChange({ value: ADD_NEW_VALUE });
          }
        },
        onError: (err) => {
          // Surface a failed soft-delete instead of failing silently (ROAR-4511 bug: guard fix + onError).
          popup({
            title: 'Error',
            variant: 'error',
            description:
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              err?.message ||
              'Failed to delete scenario.',
          });
        },
      });
    });
  };

  const scenarioOptions = useMemo(() => {
    // A saved scenario can arrive keyed by `feature_name` instead of `name` (same inconsistency the
    // detail-select effect defends against at the `setFeatureName` fallback above). Fall back to it
    // for the label, and drop any record with neither field rather than rendering a blank row —
    // mirroring the candidate normalizer in `hooks/siembagpt.js`.
    // ROAR-4919: collapse pre-existing duplicate names so the dropdown never shows the same
    // scenario twice (legacy rows created before duplicate prevention landed).
    const options = dedupeScenarioOptions(scenariosData?.scenarios || [])
      .filter((scenario) => scenario.name || scenario.feature_name)
      .map((scenario) => {
        const displayName = scenario.name || scenario.feature_name;
        return {
          label: displayName,
          value: scenario.id,
          id: scenario.id,
          name: displayName,
          badge: <ScenarioStatusBadge status={scenario.last_run_status} />,
          onDelete: () => handleDeleteClick(scenario),
        };
      });
    options.push({
      label: 'Add New Scenario',
      value: ADD_NEW_VALUE,
    });
    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenariosData]);

  const trimmedLength = scenarioTrimmedLength(description);
  const isBelowMinimum = !isScenarioDescriptionValid(description);
  const exhausted = attemptsExhausted(attemptsRemaining);

  const canValidate =
    !isDisabled && !confirmed && !exhausted && !isBelowMinimum && !validateScenario.isLoading;

  // ROAR-4919: block confirming a scenario whose (user-editable) name duplicates an existing one for
  // this application. Exclude the selected saved row only when editing it in place — not for Add-New
  // or Save-as-new, which must collide against every row including the source.
  const scenarioList = scenariosData?.scenarios;
  const editingExcludeId =
    selectedValue !== ADD_NEW_VALUE && saveMode !== 'new' ? selectedValue : null;
  // Running a previously-saved scenario with its name UNCHANGED must never be blocked — even when a
  // legacy duplicate of that name still exists in the DB (the very rows this dedup guards against, and
  // which the dropdown hides but the raw list still carries). Only enforce the block for a genuinely
  // new or renamed name.
  const selectedSavedName = scenarioDetail?.scenario
    ? scenarioFieldsFromSavedRow(scenarioDetail.scenario).featureName
    : null;
  const isExistingNameUnchanged =
    editingExcludeId != null &&
    Boolean(scenarioDetail?.scenario) &&
    normalizeScenarioName(featureName) === normalizeScenarioName(selectedSavedName);
  const nameCollision =
    !isExistingNameUnchanged && scenarioNameCollides(featureName, scenarioList, editingExcludeId);
  const selectedCandidateName = selectedScenarioCandidate(
    candidates,
    selectedCandidateIndex
  )?.feature_name;
  const candidateCollision = scenarioNameCollides(selectedCandidateName, scenarioList, null);
  const duplicateNameMessage = (name) =>
    `A scenario named "${(name || '').trim()}" already exists for this application.`;

  const handleValidate = async () => {
    setGuardrailMessage(null);
    try {
      const payload = scenarioValidatePayload({
        nonce,
        chatId,
        description,
        scenarioId: selectedValue !== ADD_NEW_VALUE ? selectedValue : null,
        username,
        password,
        mfaSecretKey,
        savedCredential,
      });
      const res = await validateScenario.mutateAsync(payload);
      setAttemptsRemaining((prev) => nextAttemptsRemaining(prev, res));
      // Overwrite (not append-if-nonempty) whenever the body carries the ledger: since ROAR-4986 the
      // server prunes legacy blocked rows, so the list can legitimately SHRINK or empty. A response
      // without a count is "unknown" and must leave the list alone.
      if (Number.isFinite(res.attempts_remaining) && Array.isArray(res.previous_attempts)) {
        setPreviousAttempts(res.previous_attempts);
      }

      if (!res.success) {
        setGuardrailMessage(
          res.message || 'This scenario could not be validated. Please revise and try again.'
        );
        return;
      }

      if (hasScenarioCandidates(res.candidates)) {
        // AC6 (ROAR-4842): the input spans multiple features — surface the picker instead of a
        // single result. Checked BEFORE the `!res.feature_name` guard below: a split response's
        // top-level name describes the whole (multi-feature) input and may legitimately be empty,
        // which must not be mis-reported as "GenPT could not name this scenario".
        setCandidates(res.candidates);
        setSelectedCandidateIndex(0);
        setFeatureName('');
        setExtractedSteps([]);
        return;
      }

      if (!res.feature_name) {
        // Treat an empty feature_name on success as a failed attempt, not a nameless confirm (plan §5).
        setGuardrailMessage('GenPT could not name this scenario. Please revise and try again.');
        return;
      }

      setFeatureName(res.feature_name);
      setExtractedSteps(res.steps || []);
    } catch (err) {
      // Transport failure / 5xx — retryable, and must NOT consume an attempt (plan §5).
      setGuardrailMessage(
        err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.'
      );
    }
  };

  // Returns to the input screen with the ORIGINAL description prefilled (AC6: "or types a revised
  // description") — `description` is only ever overwritten on Confirm, never while a candidate is
  // merely selected, so it still holds the user's original free text here. The proposed name, steps
  // and any candidate split are cleared so `hasExtractedResult` falls back to the editable input,
  // not just un-confirmed.
  const clearExtractedScenario = () => {
    setExtractedSteps([]);
    setFeatureName('');
    setConfirmed(false);
    setCandidates([]);
    setSelectedCandidateIndex(0);
    setEditingCandidateIndex(null);
    setSaveMode(null);
  };

  const handleEditScenario = () => {
    // ROAR-4986: at 0 remaining, editing is a one-way door — clearing the validated scenario drops
    // the user straight onto the "No Attempts Left" dead end with nothing to confirm. Ask first.
    if (attemptsExhausted(attemptsRemaining)) {
      // ROAR-4925: `popup()` resolves ONLY on OK (never on Cancel/close), so the destructive action
      // goes unconditionally inside `.then()` — never branch on the resolved value, which is
      // `undefined` even on OK.
      popup({
        title: 'Discard this scenario?',
        variant: 'warning',
        description:
          'You have no attempts left. Editing clears the validated scenario, and you will have to start a new test to try again.',
        okayText: 'Discard and edit',
        cancelText: 'Keep scenario',
      }).then(() => {
        clearExtractedScenario();
      });
      return;
    }
    clearExtractedScenario();
  };

  const handleStartOver = () => {
    // ROAR-4930: redirect to the GenPT Start Test page, reusing the same navigation as
    // TestTerminateWidget's handleTryAgain. Navigating unmounts this section, so the previous
    // local-state reset is redundant.
    navigate(`${mainUrl}/genpt/tests/start`);
  };

  // AC6 / ROAR-4937: confirming a candidate RE-VALIDATES that sub-scenario's self-contained
  // description through the guardrail. `candidate.description` is written by the guardrail to be
  // submittable on its own, so re-validating it yields the chosen sub-scenario's own feature_name and
  // steps AND (crucially) overwrites the parent multi-feature scenario in api-backend's Redis
  // `metadata.scenario`. That is what makes only the chosen sub-scenario execute and get persisted
  // downstream, and what gives the confirmed steps card real steps to render. It is attempt-exempt
  // (`candidateConfirmation`): the user is committing a scenario the system proposed, not spending one
  // of their 5 revisions.
  const handleConfirmCandidate = async () => {
    const candidate = selectedScenarioCandidate(candidates, selectedCandidateIndex);
    if (!candidate) return;
    // ROAR-4919: block confirming a sub-scenario whose (edited) name duplicates an existing one.
    if (candidateCollision) return;
    setGuardrailMessage(null);
    try {
      const payload = scenarioValidatePayload({
        nonce,
        chatId,
        description: candidate.description,
        scenarioId: selectedValue !== ADD_NEW_VALUE ? selectedValue : null,
        username,
        password,
        mfaSecretKey,
        savedCredential,
        candidateConfirmation: true,
      });
      const res = await validateScenario.mutateAsync(payload);
      // ROAR-4986: re-sync off this verdict too. The confirm itself is attempt-exempt, but the chip
      // must still follow the server — this is the one validate path that used to ignore the count
      // entirely, so any drift survived it.
      setAttemptsRemaining((prev) => nextAttemptsRemaining(prev, res));
      if (Number.isFinite(res.attempts_remaining) && Array.isArray(res.previous_attempts)) {
        setPreviousAttempts(res.previous_attempts);
      }

      if (!res.success) {
        // Blocked (rare — the system proposed this sub-scenario). Keep the picker open so the user can
        // choose another or edit; the attempt is not consumed (api-backend exempts candidate confirms).
        setGuardrailMessage(
          res.message || 'This scenario could not be validated. Please choose another or edit it.'
        );
        return;
      }

      if (hasScenarioCandidates(res.candidates)) {
        // Defensive: a single sub-scenario should not split again, but if the guardrail returns a new
        // split, re-surface the picker with those candidates rather than confirming a multi-feature one.
        setCandidates(res.candidates);
        setSelectedCandidateIndex(0);
        setEditingCandidateIndex(null);
        return;
      }

      if (!res.feature_name) {
        setGuardrailMessage(
          'GenPT could not name this scenario. Please choose another or edit it.'
        );
        return;
      }

      // Land the re-validated child in the SAME un-confirmed state a single-scenario validate produces:
      // clearing `candidates` drops the picker and the extracted-steps preview takes over, showing the
      // chosen sub-scenario's OWN steps (ROAR-4937 trailing bug) with the ordinary CONFIRM SCENARIO
      // button (`handleConfirm`). We do NOT auto-confirm — the user reviews the steps first, exactly
      // like the single-scenario flow.
      setFeatureName(res.feature_name);
      setDescription(candidate.description);
      setExtractedSteps(res.steps || []);
      setCandidates([]);
      setEditingCandidateIndex(null);
    } catch (err) {
      // Transport failure / 5xx — retryable, and does NOT consume an attempt (api-backend + this
      // caller both guard). Keep the picker open.
      setGuardrailMessage(
        err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.'
      );
    }
  };

  const handleConfirm = async () => {
    // ROAR-4919: never confirm a duplicate name — defends against a keyboard activation that
    // bypasses the disabled button, since this optimistically sets confirmed=true below.
    if (nameCollision) return;
    // AC11: confirming a SAVED scenario whose description was edited must ask whether to update the
    // existing record or save a new one. Add-New, and a Run-Again with an unchanged description,
    // skip the prompt (nothing to reconcile) and leave saveMode null.
    const savedScenario = scenarioDetail?.scenario;
    const isSavedSelection = selectedValue !== ADD_NEW_VALUE && Boolean(savedScenario);
    const originalDescription = (savedScenario?.description || '').trim();
    const wasEdited = isSavedSelection && description.trim() !== originalDescription;

    if (wasEdited) {
      // ROAR-4925: the shared `popup()` only ever resolves on its OK button and NEVER on Cancel /
      // close (SuccessOrFailedModal onClose closes without resolving). Awaiting its value here meant
      // "Save as new" (the Cancel button) hung forever — `setConfirmed(true)` never ran, so Continue
      // stayed disabled and no test (and thus no scenario save) ever started; and "Update existing"
      // (OK) resolved `undefined`, which the `? 'update' : 'new'` mapping read as 'new', silently
      // duplicating the row instead of updating it. Commit the default ("save as new") up front so
      // the un-resolving Cancel path is still a confirmed scenario, then upgrade to 'update' only
      // when OK actually fires.
      setSaveMode('new');
      setConfirmed(true);
      popup({
        title: 'Update saved scenario?',
        variant: 'warning',
        description: `You changed the description for "${
          savedScenario.name || featureName
        }". Update the existing saved scenario, or save this as a new one?`,
        okayText: 'Update existing',
        cancelText: 'Save as new',
      }).then(() => {
        setSaveMode('update');
      });
      return;
    }
    setSaveMode(null);
    setConfirmed(true);
  };

  // Notify the parent on every meaningful change so TestSetup can gate Continue and fold the confirmed
  // fields into configData (AC7).
  useEffect(() => {
    // 'Save as new' drops the saved id so the backend creates a fresh row; 'update' and Run-Again
    // keep it (the backend updates in place only when saveMode==='update', AC11).
    const effectiveScenarioId =
      saveMode === 'new' ? null : selectedValue !== ADD_NEW_VALUE ? selectedValue : null;
    onChange({
      confirmed,
      ...confirmedScenarioFields({
        scenarioId: effectiveScenarioId,
        saveMode,
        description,
        featureName,
        steps: extractedSteps,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmed, selectedValue, description, featureName, extractedSteps, saveMode]);

  if (isReadOnly) {
    return (
      <div className="mt-4">
        <div className="tracking-wide font-semibold text-black mb-1">Define Scenario</div>
        <div className="text-sm">{featureName || 'Scenario not yet confirmed'}</div>
        {description && <div className="text-sm opacity-70 mt-1">{description}</div>}
        {/* View Setup Details: surface the planned scenario steps read-only, mirroring the
            interactive extracted-steps list below (~L803). Steps come from `initialScenario.steps`
            (Mongo session.scenario.steps for Validate runs; genpt_scenarios.steps for reuse). */}
        {extractedSteps.length > 0 && (
          <div className="mt-3 space-y-2">
            {extractedSteps.map((step, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <span className="shrink-0 rounded-[10px] bg-primary-50 text-primary-500 px-2 py-0.5 text-xs font-semibold whitespace-nowrap">
                  Step {idx + 1}
                </span>
                <span>{step}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // AC6 (ROAR-4842): the picker takes precedence over the single-scenario steps card while a split
  // is un-confirmed. Once CONFIRM SCENARIO commits a candidate, `candidates` is cleared and this
  // falls back to the ordinary confirmed steps-card view — see `handleConfirmCandidate`.
  const showCandidatePicker = hasScenarioCandidates(candidates) && !confirmed;
  const hasExtractedResult =
    confirmed || extractedSteps.length > 0 || featureName || candidates.length > 0;

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-2">
        <img src="/images/genpt/accesslevel.svg" alt="Define Scenario" className="w-6 h-6" />
        <div className="tracking-wide font-semibold text-black">Define Scenario</div>
      </div>
      <div className="text-sm opacity-70 mb-3">
        Instruct GenPT exactly how to navigate the target flow.
      </div>

      <div className="mb-3">
        <div className="text-sm opacity-70 mb-1">
          Choose from saved scenarios or select &#8220;Add New&#8221; in dropdown to create.
        </div>
        <Dropdown
          options={scenarioOptions}
          placeholder="Select Scenario"
          name="scenario"
          isMulti={false}
          value={scenarioOptions.find((o) => String(o.value) === String(selectedValue)) || null}
          onChange={handleSelectChange}
          isDisabled={isDisabled || confirmed}
        />
      </div>

      {!hasExtractedResult && (
        <div className="border border-[#EFEFEF] rounded-lg p-3">
          {/* Tips toggle (Figma 02b) top-left, attempts chip top-right (Figma 05a). The tips panel
              renders full-width below this row (ROAR-4950) so it matches the textarea width instead
              of being squeezed into the toggle's flex column. */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <ScenarioTipsToggle expanded={showTips} onToggle={() => setShowTips((prev) => !prev)} />
            <AttemptsChip count={attemptsRemaining} />
          </div>
          {showTips && <ScenarioTipsPanel />}

          {validateScenario.isLoading ? (
            <ScenarioValidatingLoader />
          ) : (
            <>
              {/* The field keeps its own border so it reads as an input (Figma 03) — red once the
                  user has typed but is still below the minimum, neutral while empty. */}
              <MuiTextField
                placeholder="e.g. Navigate to the {URL}, access the {webpage name} and test the {particular flow}."
                multiline
                rows={5}
                margin="none"
                value={description}
                error={isBelowMinimum && trimmedLength > 0}
                inputProps={{
                  maxLength: SCENARIO_MAX_CHARS,
                  'aria-label': 'Describe your business scenario',
                  'aria-describedby': 'scenario-char-count',
                }}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setGuardrailMessage(null);
                }}
                disabled={isDisabled || confirmed}
              />
              <div className="flex items-center justify-between gap-3 mt-1">
                <span className="text-xs text-grey3">5 validated scenarios allowed per test</span>
                <div
                  id="scenario-char-count"
                  role="status"
                  aria-live="polite"
                  className={`text-xs ${
                    isBelowMinimum ? 'text-functional-red font-semibold' : 'text-grey3'
                  }`}
                >
                  {`${trimmedLength}/${SCENARIO_MIN_CHARS} characters`}
                  {isBelowMinimum &&
                    ' — Please add more detail about the scenario you want to test.'}
                </div>
              </div>
            </>
          )}

          {/* Guardrail / exhausted error, rendered in place inside the field (Figma 05a). */}
          {exhausted ? (
            <div className="mt-3">
              <ErrorBanner
                id="scenario-guardrail-error"
                message="No Attempts Left: You've used all 5 attempts for this scenario. Start a new test to keep testing this flow."
              />
            </div>
          ) : (
            guardrailMessage && (
              <div className="mt-3">
                <ErrorBanner id="scenario-guardrail-error" message={guardrailMessage} />
              </div>
            )
          )}

          {previousAttempts.length > 0 && (
            <div className="mt-2">
              <PreviousAttempts
                attempts={previousAttempts}
                expanded={showPreviousAttempts}
                onToggle={() => setShowPreviousAttempts((prev) => !prev)}
              />
            </div>
          )}

          {/* Footer action bar (Figma 02/05). Button greys out while validating. */}
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#EFEFEF]">
            {exhausted ? (
              <Button
                type="button"
                onClick={handleStartOver}
                className="!bg-primary-500 hover:!bg-primary-600 !text-white px-4"
              >
                START OVER THE TEST
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleValidate}
                disabled={!canValidate}
                className={`px-4 ${
                  canValidate
                    ? '!bg-primary-500 hover:!bg-primary-600 !text-white cursor-pointer'
                    : '!bg-gray-50 !text-gray-500 cursor-not-allowed'
                }`}
              >
                VALIDATE SCENARIO
              </Button>
            )}
          </div>
        </div>
      )}

      {showCandidatePicker &&
        (validateScenario.isLoading ? (
          // ROAR-4937: confirming a candidate re-validates it through the guardrail — reuse the same
          // loader the initial validate shows so the picker doesn't sit frozen during the round-trip.
          <ScenarioValidatingLoader />
        ) : (
          <>
            <div className="mt-2">
              <ScenarioTipsToggle
                expanded={showTips}
                onToggle={() => setShowTips((prev) => !prev)}
              />
              {showTips && <ScenarioTipsPanel />}
            </div>

            <div
              role="status"
              className="mt-3 rounded-md border border-[#FCE38A] bg-[#FFF8E1] px-3 py-2 text-sm text-[#8A6D3B]"
            >
              <span className="font-semibold">More than 1 scenario detected:</span> GenPT can run
              only one scenario per test. Choose one scenario to test right now and the rest stay
              saved, and you can run them in a new test anytime.
            </div>

            <div className="text-sm font-semibold opacity-70 mt-4 mb-2">
              Choose the scenario to test
            </div>
            <div className="space-y-2">
              {candidates.map((candidate, idx) => {
                const selected = idx === selectedCandidateIndex;
                return (
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                  <div
                    key={idx}
                    role="radio"
                    tabIndex={0}
                    aria-checked={selected}
                    aria-label={`Choose ${candidate.feature_name}`}
                    onClick={() => setSelectedCandidateIndex(idx)}
                    onKeyDown={(e) => {
                      // Only handle Enter/Space when the ROW ITSELF is the event target — not when
                      // the keydown bubbled up from the nested rename input or Edit-name button. Not
                      // guarding this would call preventDefault() on every keystroke typed into the
                      // rename field (silently swallowing spaces) and would hijack a keyboard Enter/
                      // Space meant for the nested Edit-name button into a row-selection instead.
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedCandidateIndex(idx);
                      }
                    }}
                    className={`rounded-lg border p-3 cursor-pointer ${
                      selected ? 'border-primary-500 bg-primary-50' : 'border-[#EFEFEF]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {selected ? (
                        <RadioButtonCheckedIcon
                          sx={{ color: '#12AFB8', fontSize: 20 }}
                          className="flex-shrink-0"
                        />
                      ) : (
                        <RadioButtonUncheckedIcon
                          sx={{ color: '#A1A5B7', fontSize: 20 }}
                          className="flex-shrink-0"
                        />
                      )}
                      {editingCandidateIndex === idx ? (
                        <MuiTextField
                          value={candidate.feature_name}
                          inputProps={{ maxLength: SCENARIO_FEATURE_NAME_MAX_CHARS }}
                          onChange={(e) =>
                            setCandidates((prev) =>
                              renameScenarioCandidate(prev, idx, e.target.value)
                            )
                          }
                          onBlur={() => setEditingCandidateIndex(null)}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      ) : (
                        <>
                          <span className="font-semibold text-black">{candidate.feature_name}</span>
                          <button
                            type="button"
                            aria-label={`Edit ${candidate.feature_name} name`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingCandidateIndex(idx);
                            }}
                            className="text-grey3 hover:text-primary-500"
                          >
                            <EditIcon fontSize="small" />
                          </button>
                        </>
                      )}
                    </div>
                    <div className="text-sm opacity-70 ml-7">{candidate.description}</div>
                  </div>
                );
              })}
            </div>

            {/* Guardrail error from a candidate re-validate (ROAR-4937) — rare, since the candidate was
              system-proposed; the picker stays open so the user can choose another or edit. */}
            {guardrailMessage && (
              <div className="mt-3">
                <ErrorBanner id="scenario-guardrail-error" message={guardrailMessage} />
              </div>
            )}

            {/* ROAR-4919: duplicate-name guard for the selected sub-scenario. */}
            {candidateCollision && (
              <div className="mt-3">
                <ErrorBanner
                  id="scenario-candidate-name-duplicate"
                  message={duplicateNameMessage(selectedCandidateName)}
                />
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <Button
                type="button"
                onClick={handleConfirmCandidate}
                disabled={isDisabled || candidateCollision}
                className="!bg-primary-500 hover:!bg-primary-600 !text-white px-4"
              >
                CONFIRM SCENARIO
              </Button>
              <Button
                type="button"
                onClick={handleEditScenario}
                disabled={isDisabled}
                className="!border !border-primary-500 !text-primary-500 !bg-white px-4"
              >
                EDIT SCENARIO
              </Button>
            </div>
          </>
        ))}

      {hasExtractedResult && !showCandidatePicker && (
        <div className="mt-4 border border-[#EFEFEF] rounded-lg overflow-hidden">
          {/* Header band (Figma 06/10): teal title left, yellowish attempts pill right. */}
          <div className="flex items-center gap-2 bg-primary-50 px-4 py-2">
            <span className="flex items-center gap-1 text-sm font-semibold text-primary-500">
              GenPT Extracted Steps
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </span>
            <AttemptsChip count={attemptsRemaining} />
          </div>

          <div className="p-4">
            {confirmed ? (
              // Confirmed — collapse to a single row (Figma frame 10).
              <div className="flex items-center justify-between gap-2 border border-[#EFEFEF] rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon
                    sx={{ color: '#3BB54A', fontSize: 20 }}
                    className="flex-shrink-0"
                  />
                  <span className="font-semibold text-black">
                    Scenario Confirmed - {featureName}
                  </span>
                </div>
                <button
                  type="button"
                  aria-label="Edit confirmed scenario"
                  onClick={() => setConfirmed(false)}
                  className="text-grey3 hover:text-primary-500"
                >
                  <EditIcon fontSize="small" />
                </button>
              </div>
            ) : (
              // Un-confirmed preview (Figma frame 06).
              <>
                <div className="text-xs opacity-70 mb-1">Name of the scenario:</div>
                {editingName ? (
                  <div className="mb-4">
                    <MuiTextField
                      value={featureName}
                      onChange={(e) => setFeatureName(e.target.value)}
                      onBlur={() => setEditingName(false)}
                      autoFocus
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 border border-[#EFEFEF] rounded-lg px-3 py-2 mb-4">
                    <span className="font-semibold text-black">{featureName}</span>
                    <button
                      type="button"
                      aria-label="Edit scenario name"
                      onClick={() => setEditingName(true)}
                      className="text-grey3 hover:text-primary-500"
                    >
                      <EditIcon fontSize="small" />
                    </button>
                  </div>
                )}

                {/* ROAR-4919: duplicate-name guard for the scenario name. */}
                {nameCollision && (
                  <div className="mb-4 -mt-2">
                    <ErrorBanner
                      id="scenario-name-duplicate"
                      message={duplicateNameMessage(featureName)}
                    />
                  </div>
                )}

                <div className="text-xs opacity-70 mb-2">
                  Confirm if the extracted steps match your intended scenario
                </div>
                <div className="space-y-2">
                  {extractedSteps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <span className="shrink-0 rounded-[10px] bg-primary-50 text-primary-500 px-2 py-0.5 text-xs font-semibold whitespace-nowrap">
                        Step {idx + 1}
                      </span>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 mt-4">
                  <Button
                    type="button"
                    onClick={handleConfirm}
                    disabled={isDisabled || nameCollision}
                    className="!bg-primary-500 hover:!bg-primary-600 !text-white px-4"
                  >
                    CONFIRM SCENARIO
                  </Button>
                  <Button
                    type="button"
                    onClick={handleEditScenario}
                    disabled={isDisabled}
                    className="!border !border-primary-500 !text-primary-500 !bg-white px-4"
                  >
                    EDIT SCENARIO
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
